import { CdkDragDrop, CdkDragMove, CdkDragStart } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  output,
  SimpleChanges,
} from '@angular/core';
import { addMinutes, isSameDay } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { BehaviorSubject, Observable } from 'rxjs';

import { markEventAsHandled } from '@blaze-runtime/engine-util';
import {
  isElementOrChildOf,
  useEmitTracker,
  type ValueWithUnitOfMeasure,
} from '@blaze-shared/shared-util';

import {
  type Calendar,
  CalendarConfig,
  CalendarEvent,
  CalendarTimeFormat,
  EventChangeEvent,
  EventChangeEventBase,
} from '../calendar.models';
import { CalendarDayViewLayoutService } from '../layout/calendar-day-view-layout.service';
import { CalendarEventLayout } from '../layout/calendar-event-layout.model';
import { CALENDAR_CONFIG } from '../providers/config';

@Component({
  selector: 'blaze-calendar-day-view',
  templateUrl: './calendar-day-view.component.html',
  styleUrls: ['./calendar-day-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CalendarDayViewComponent implements OnChanges {
  // this prop needs to be static so that all instances toggle their state
  static readonly isDragging$$ = new BehaviorSubject<boolean>(false);
  static scrolledToMorning = false;

  private readonly el = inject<ElementRef<HTMLDivElement>>(ElementRef);
  private readonly layoutService = inject(CalendarDayViewLayoutService);

  readonly config = inject<CalendarConfig>(CALENDAR_CONFIG);

  add = output<Partial<CalendarEvent>>();
  delete = output<CalendarEvent>();
  details = output<CalendarEvent>();
  edit = output<CalendarEvent>();
  eventChange = output<EventChangeEvent>();
  eventDrop = output<CdkDragDrop<any, any, CalendarEvent>>();

  @Input() calendar?: Calendar;
  @Input() events: CalendarEvent[] = [];
  @Input() timeZone: string | null = null;
  @Input() selectedDate: Date | undefined;
  @Input() defaultDurationMinutes = 60;
  @Input() timeFormat: CalendarTimeFormat = CalendarTimeFormat['12H'];
  @Input() isAddEnabled = false;
  @Input() isEditEnabled = false;
  @Input() isDeleteEnabled = false;
  @Input() isDetailsViewEnabled = false;
  @Input() isEventDragDisabled = false;
  // [index of the view, views count]
  @Input() scrollAllDayToHour?: { idx: number; count: number };
  @Input() availableHours: { min: number; max: number } = { min: 0, max: 24 };
  @Input() timeScale = 1;
  @Input() eventFontSize: ValueWithUnitOfMeasure = '13px';
  @Input() headerHeightPx = 0;

  private readonly updatePositionsTracker = useEmitTracker();

  readonly paddingPx = 1;
  readonly quarterMinutes = 15;
  readonly quarters = [0, 15, 30, 45];

  layoutEvents: CalendarEventLayout[] = [];
  hours = new Array(24).fill(null).map((_, index) => index);
  activeHours: Partial<Record<number, Record<number, boolean>>> = {};

  get isDragging$(): Observable<boolean> {
    return CalendarDayViewComponent.isDragging$$.asObservable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['events']) {
      this.generateLayoutEvents();
    }

    if (changes['availableHours']) {
      this.regenerateHours();
      this.generateLayoutEvents();
      if (
        !CalendarDayViewComponent.scrolledToMorning &&
        !!this.selectedDate &&
        isSameDay(new Date(), this.selectedDate)
      ) {
        CalendarDayViewComponent.scrolledToMorning = true;
        setTimeout(() => {
          const now = new Date();
          const hour = now.getHours();
          if (hour < this.availableHours.min || hour >= this.availableHours.max) {
            return;
          }
          const timestampMarker = Math.round(
            ((hour - this.availableHours.min + now.getMinutes() / 60) * this.config.oneHourHeight) /
              this.timeScale,
          );
          const container = this.el.nativeElement.closest('.viewport:has(.calendar-workspace)');
          if (!container) {
            return;
          }
          const top = timestampMarker - (container.clientHeight - this.headerHeightPx) / 2;
          container.scrollTo({ behavior: 'smooth', top });
        });
      }
    }
  }

  trackById(layout: CalendarEventLayout): string {
    return layout.event.id + (layout.event.partial ?? '');
  }

  onAddClick(hours: number, minutes: number): void {
    if (!this.isAddEnabled) {
      return;
    }

    let startDate = this.selectedDate ? new Date(this.selectedDate) : new Date();
    startDate = this.gridToDate(startDate, hours, minutes);
    startDate.setSeconds(0);
    startDate.setMilliseconds(0);

    // Move the hour into the timezone
    startDate = fromZonedTime(
      startDate,
      this.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    );

    const endDate = addMinutes(new Date(startDate), this.defaultDurationMinutes);

    this.add.emit({
      startDate,
      endDate,
    });
  }

  onQuarterMouseOver(hours: number, minutes: number): void {
    const activeHours: Record<number, Record<number, boolean>> = {};
    const time = this.gridToDate(new Date(), hours, minutes);
    const startingDate = time.getDate();

    let currentHours = hours;
    let currentMinutes = minutes;
    for (let i = 0; i < this.defaultDurationMinutes; i += this.quarterMinutes * this.timeScale) {
      activeHours[currentHours] ??= {};
      activeHours[currentHours][currentMinutes] = true;

      currentMinutes += this.quarterMinutes;
      if (currentMinutes === 60) {
        currentHours += this.timeScale;
        currentMinutes = 0;
      }

      time.setMinutes(time.getMinutes() + this.quarterMinutes);

      if (startingDate !== time.getDate()) {
        break;
      }
    }

    this.activeHours = activeHours;
  }

  onQuarterMouseLeave(): void {
    this.activeHours = {};
  }

  onEventClick(event: CalendarEvent): void {
    if (this.isDetailsViewEnabled) {
      this.details.emit(event);
      return;
    }

    if (this.isEditEnabled) {
      this.edit.emit(event);
    }
  }

  onEventChange($event: EventChangeEventBase, event: CalendarEvent): void {
    this.eventChange.emit({ ...$event, event });
  }

  onDragMouseDown(event: MouseEvent): void {
    if (
      isElementOrChildOf(event.target as HTMLElement, (e) => e.classList.contains('action-button'))
    ) {
      return;
    }

    CalendarDayViewComponent.isDragging$$.next(true);
    markEventAsHandled(event);
  }

  onDragMouseUp(): void {
    CalendarDayViewComponent.isDragging$$.next(false);
  }

  onDragStart(event: CdkDragStart): void {
    markEventAsHandled(event.event);
    event.event.stopPropagation();
    event.source.getPlaceholderElement().style.zIndex = '1200';
  }

  onDragMoved(event: CdkDragMove): void {
    event.event.stopPropagation();
  }

  onEventMoved(delta: { h: number; m: number }, event: CalendarEvent): void {
    this.eventChange.emit({ changeType: 'both', delta, event });
  }

  onDragDropped(event: CdkDragDrop<any, any, CalendarEvent>): void {
    this.eventDrop.emit(event);
    CalendarDayViewComponent.isDragging$$.next(false);
  }

  onEditClick(event: CalendarEvent): void {
    this.edit.emit(event);
  }

  onDeleteClick(event: CalendarEvent): void {
    this.delete.emit(event);
  }

  private generateLayoutEvents(): void {
    if (!this.updatePositionsTracker.shouldEmit({ e: this.events, ah: this.availableHours })) {
      return;
    }

    const fractionalHours = (date: Date): number => date.getHours() + date.getMinutes() / 60;
    this.layoutEvents = this.layoutService.positionEvents(
      this.events.filter(
        (event) =>
          fractionalHours(event.startDate) >= this.availableHours.min &&
          fractionalHours(event.startDate) < this.availableHours.max,
      ),
    );
  }

  private regenerateHours(): void {
    const scaledMin = this.availableHours.min / this.timeScale;
    const scaledMax = this.availableHours.max / this.timeScale;
    this.hours = new Array(scaledMax - scaledMin)
      .fill(null)
      .map((_, index) => index * this.timeScale + this.availableHours.min);
  }

  private gridToDate(baseDate: Date, gridHours: number, gridMinutes: number): Date {
    baseDate.setHours(Math.floor(gridHours));
    baseDate.setMinutes(gridMinutes * this.timeScale + 60 * (gridHours - Math.floor(gridHours)));
    return baseDate;
  }
}
